const axios = require("axios");
const _set = require('lodash/set');

module.exports = {
    resetForm,
    validateForm,
    catchSubmit,
    validateInputsOnChange,
    focusFirst,
    serializeObject,
    syncCheckboxGroups,
    syncContactRequirements,
    upload,
    applyConditionalVisibility
};

/**
 * Clears form inputs and sets class validation to a pristine state
 *
 * @param form - Form to reset
 */
function resetForm(form) {
    form = $(form);
    let formContainer = $(form[0].parentNode);
    let $dealershipSelect = formContainer.find('select[name=dealershipId]');

    // Reset the form inputs
    form[0].reset();

    // Resets dealership dropdown if it's not hidden
    if ($dealershipSelect && $dealershipSelect.length > 0 && !$dealershipSelect.attr('hidden')) {
        let hiddenDealershipId = formContainer.find('input[name=dealershipId]');
        hiddenDealershipId.val('');
    }

    formContainer
        .removeClass('form--pending')
        .removeClass('form--success')
        .removeClass('form--failed');

    form.removeClass('submitted');
    // Clear validation classes
    form.find('.dirty, invalid', function (input) {
        $(input).removeClass('dirty').removeClass('invalid').addClass('pristine');
    });
    form.find('[data-checkbox-group-option]').removeClass('dirty').removeClass('invalid').addClass('pristine');
    syncCheckboxGroups(form);
    syncContactRequirements(form);
}

/**
 * Convert the form data (through serializeArray) into an object with prop as the input name and value as the input value
 *
 * @param form - The form to serialise
 */
function serializeObject(form) {
    let $form = $(form);
    syncCheckboxGroups($form);

    // Convert the serialize array into an object with prop as the input name and value as the input value
    let body = {};
    const data = $form.serializeArray();

    // find every radio-group name in the form
    const radioNames = Array.from(new Set($form.find('input[type="radio"]').map((i, el) => el.name).get()));

    radioNames.forEach(name => {
        if (!data.some(d => d.name === name)) {
            data.push({name, value: '—'});
        }
    });

    data.forEach(input => {
        const $el = $form.find(`[name="${input.name}"]`).first();
        if ($el.attr('type') !== 'file') {
            _set(body, input.name, input.value || '—');
        }
    });

    // Native checkboxes omit unchecked values; Fusion consent must explicitly retain false.
    $form.find('[data-boolean-switch]').forEach(input => {
        if (!input.disabled) _set(body, input.name, input.checked);
    });

    return body;
}

/**
 * Catches a submit function, sets form validation classes and calls an onSuccess or onFail function depending on if
 * the form validation passed.
 *
 * onSuccess receives the form submitted and an object of all form inputs as {name: value, .., ..}
 * onFail receives the form submitted
 *
 * @param forms - Form to catch submits
 * @param done - Callback for when the form is valid of invalid
 */
function catchSubmit(forms, done) {
    $(forms).on('submit', function (e) {
        e.preventDefault();

        let $form = $(this);
        if (validateForm($form)) {
            done(null, serializeObject($form));
        } else {
            $form.addClass('submitted');

            focusFirst($form, true);

            done(true);
        }
    })
}

/**
 * Loops through all form inputs and selects
 *
 * @param form - The form to validate
 * @returns {boolean} - True if all inputs and selects are valid
 */
function validateForm(form) {
    let valid = true;
    form = $(form);

    syncCheckboxGroups(form);
    syncContactRequirements(form);

    form.find('input, select, textarea').forEach(function (input) {
        input = $(input);

        // Make the inputs, pristine
        input.removeClass('dirty').addClass('pristine');

        if (!input[0].validity.valid) {
            valid = false;
            input.addClass('invalid');
        } else {
            input.removeClass('invalid');
        }
    });

    syncCheckboxGroups(form, true);

    return valid;
}

/**
 * Loops through all form inputs and selects adding class validation when they change.
 *
 * dirty - the input has been changed since the last form reset / submit
 * pristine - the input has NOT been changed since the last form reset / submit
 * invalid - the input is invalid
 *
 * @param form - the form to validate inputs on change
 */
function validateInputsOnChange(form) {
    form = $(form);

    syncContactRequirements(form);
    form.on('change input', '[name="customer.contactNumber"]', () => syncContactRequirements(form));

    form.find('[data-checkbox-group-option]').off('change.checkboxGroup').on('change.checkboxGroup', function () {
        syncCheckboxGroups(form);
    });

    form.find('input, select, textarea').forEach(function (input) {
        input = $(input);

        input.on('change', function () {
            // Set dirty
            input.addClass('dirty').removeClass('pristine');

            // Set validity
            if (!input[0].validity.valid) {
                input.addClass('invalid');
            }
        })
    });
}

/** Keep the email-or-phone policy in native validation for converted forms. */
function syncContactRequirements(form) {
    const element = $(form)[0];
    if (!element) return;
    element.querySelectorAll('[data-required-unless]').forEach(input => {
        const other = element.elements.namedItem(input.getAttribute('data-required-unless'));
        input.required = !other?.value?.trim();
    });
}

/**
 * Sync grouped checkbox controls into their hidden field and native validation state.
 *
 * @param form - Form containing checkbox groups
 * @param {boolean=} reapplyInvalidClasses True after the normal validation loop has run
 */
function syncCheckboxGroups(form, reapplyInvalidClasses) {
    $(form).find('[data-checkbox-group]').forEach(function (group) {
        let $group = $(group);
        let $options = $group.find('[data-checkbox-group-option]');
        let $validator = $group.find('[data-checkbox-group-validator]').first();
        let $value = $group.find('[data-checkbox-group-value]').first();
        let selectedValues = [];

        $options.forEach(function (option) {
            let $option = $(option);

            if ($option.prop('checked')) {
                selectedValues.push($option.val());
            }
        });

        $value.val(selectedValues.join(', '));

        let invalid = $group.attr('data-required') === 'true' && selectedValues.length === 0;

        if ($validator[0]) {
            $validator[0].setCustomValidity(invalid ? 'Please select an option.' : '');
        }

        if (invalid) {
            if (reapplyInvalidClasses || $group.closest('form').hasClass('submitted')) {
                $options.addClass('invalid');
            }
        } else {
            $options.removeClass('invalid');
        }
    });
}

/**
 * Focuses the first form input field.
 *
 * @param element The container element to look for input elements with in.
 * @param {boolean=} invalid True to focus the first invalid field.
 */
function focusFirst(element, invalid) {
    setTimeout(function () {
        let selector = !invalid ? 'input:not([type=hidden]), select, textarea' : '.invalid';
        let $firstInvalid = element.find('fieldset').find(selector).first();
        $firstInvalid.focus();

        if ($firstInvalid[0] && /text|search|password|tel|url/i.test($firstInvalid[0].type)) {
            $firstInvalid[0].setSelectionRange(0, $firstInvalid.val().length);
        }
    }, 0);
}

/**
 * Upload all files if any before submitting the form.
 *
 * @param files Files to upload
 * @param body Form body
 */
async function upload(files, body) {
    return new Promise(async (resolve, reject) => {
        // If no files to upload skip this and continue the form submission.
        if (!files || files.length === 0) resolve(body);
        for (const file of files) {
            let data = new FormData();
            if (file?.files?.[0]) {
                data.append('file', file.files[0]);
                data.append('action', 'upload');
                data.append('private', !file.getAttribute('data-public'));
                const uuid = file.getAttribute('data-dealership');
                await axios.post(`/api/v2/attachments/${uuid}`, data).then(({data}) => {
                    const name = file.getAttribute('name');
                    const url = new URL(data[0]._links.download.href);
                    const publicLink = data?.[0]?._links?.src?.href
                    body[name] = publicLink || url.pathname;
                    resolve(body)
                }).catch((err) => {
                    if (err.response) reject(err.response);
                    reject(err);
                });
            } else {
                resolve(body)
            }
        }

    });
}

/**
 * Scans for any [data-shows-when] rules in the form,
 * wires up change handlers on the dependent field, and
 * toggles visibility of the target wrapper.
 *
 */
function applyConditionalVisibility($form) {
    // find all wrappers that have a showsWhen rule
    $form.find('[data-shows-when]').each((i, wrapper) => {
        const $wrapper = $(wrapper);
        const name = $wrapper.attr('data-field-name');
        const rule = $wrapper.attr('data-shows-when').trim();

        // parse out left/right/operator: "foo.bar === true"
        const [, depName, operator, rawLiteral] = rule.match(/^(.+?)\s*(===|==|!==|!=)\s*(.+)$/);

        // evaluator for this field
        function evaluateAndToggle() {
            // find the dependent input(s)
            const $dep = $form.find(`[name="${depName}"]`);
            let currentValue;

            if ($dep.attr('type') === 'checkbox') {
                currentValue = $dep.prop('checked');
            } else if ($dep.attr('type') === 'radio') {
                currentValue = $form.find(`[name="${depName}"]:checked`).val();
            } else {
                currentValue = $dep.val();
            }

            // coerce literal
            let targetValue = rawLiteral.trim();
            if (targetValue === 'true') targetValue = true; else if (targetValue === 'false') targetValue = false; else if (!isNaN(targetValue)) targetValue = parseFloat(targetValue); else targetValue = targetValue.replace(/^['"](.*)['"]$/, '$1');

            // compare
            let visible;
            switch (operator) {
                case '===':
                    visible = currentValue === targetValue;
                    break;
                case '==':
                    visible = currentValue == targetValue;
                    break;
                case '!==':
                    visible = currentValue !== targetValue;
                    break;
                case '!=':
                    visible = currentValue != targetValue;
                    break;
            }

            $wrapper.toggle(visible);
        }

        // run once on load
        evaluateAndToggle();

        // delegate change events on the dependent field(s)
        $form.on('change input', `[name="${depName}"]`, evaluateAndToggle);
    });
}
